<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600"
    scrollable
    :fullscreen="$vuetify.display.xs"
  >
    <v-card v-cloak>
      <v-card-title class="d-flex justify-space-between">
        {{ $t('console.ftpUsersAddUser') }}
        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <v-form ref="form" v-model="valid" @submit.prevent="submitForm" autocomplete="off">
          <!-- Destination Folder Selection -->
          <div class="mb-4">
            <label class="mb-2 font-weight-medium">{{ $t('console.selectDestinationFolder') }}</label>
            <SelectNodeDialog v-model="formData.nodeId" #default="slotProps">
              <v-btn
                variant="flat" block
                color="info"
              >
                <span v-if="slotProps && slotProps.selectedNode">
                  {{ slotProps.selectedNode.name }}
                </span>
                <span v-else>
                    {{ $t('console.selectDestinationFolder') }}
                </span>
              </v-btn>
            </SelectNodeDialog>

            <v-input :rules="nodeIdRules" :model-value="formData.nodeId" hide-details="auto" class="ml-4">
            </v-input>
          </div>

          <!-- Team Member Selection -->
          <div class="mb-4">
            <label class="mb-2 font-weight-medium">{{ $t('console.selectUser') }}</label>
            <v-autocomplete
              v-model="formData.userId"
              :items="userItems"
              :rules="userIdRules"
              :placeholder="$t('console.selectUser')"
              :aria-label="$t('console.selectUser')"
              :disabled="!formData.nodeId"
              :loading="isLoadingUsers"
              item-title="name"
              item-value="id"
              variant="solo-filled"
              flat
              hide-details="auto"
              clearable
            />
          </div>

          <!-- FTP Username -->
          <div class="mb-4">
            <label class="mb-2 font-weight-medium">{{ $t('console.ftpUsername') }}</label>
            <v-alert
              type="info"
              variant="flat"
              density="compact"
              class="mt-1"
            >
              {{ $t('console.ftpUsernameGeneratedAutomatically') }}
            </v-alert>
          </div>

          <!-- Password Field with Generation (similar to AccesslinkCreateDialog) -->
          <div class="mb-4">
            <label class="mb-2 font-weight-medium">{{ $t('console.ftpPassword') }}</label>
            <v-text-field
              v-model="formData.password"
              type="text"
              :rules="passwordRules"
              :placeholder="$t('console.ftpPassword')"
              :aria-label="$t('console.ftpPassword')"
              variant="solo-filled"
              flat
              autocomplete="off"
              hide-details="auto"
            >
              <template v-slot:append-inner>
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <v-btn icon variant="text" v-bind="props" @click="generatePassword">
                      <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('routes.users.generatePassword') }}</span>
                </v-tooltip>

                <v-tooltip v-if="canAccessClipboard && formData.password && formData.password.length > 0" location="bottom">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      @click="copyPasswordToClipboard"
                      variant="text"
                      icon
                    >
                      <v-icon>mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('routes.pod.menuAccesslink.copyPassword') }}</span>
                </v-tooltip>
              </template>
            </v-text-field>
          </div>

          <!-- Error Messages -->
          <v-alert v-if="errorMessage" type="error" variant="tonal" class="mt-4">
            {{ errorMessage }}
          </v-alert>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          @click="submitForm"
          :loading="isSubmitting"
          :disabled="!valid"
        >
          {{ $t('common.create') }}
        </v-btn>
        <v-btn variant="text" @click="dialog = false">{{ $t('common.cancel') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js';
import { useUserStore } from '@/stores/UserStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useFtpUserStore } from '@/stores/FtpUserStore.js';
import Helper from '@/components/helper';
import DialogModel from '@/components/mixin/DialogModel.js';
import SelectNodeDialog from '@/routes/console/users/components/dialogs/SelectNodeDialog.vue';

export default {
  name: 'FTPUserCreateDialog',
  mixins: [DialogModel],
  components: {
    SelectNodeDialog,
  },
  data() {
    return {
      valid: false,
      isSubmitting: false,
      isLoadingUsers: false,
      errorMessage: '',
      selectedNodeRootpath: null,
      formData: {
        userId: null,
        password: '',
        nodeId: null,
      },
      userIdRules: [
        v => !!v || this.$t('console.userIsRequired'),
      ],
      passwordRules: [
        v => !!v || this.$t('console.passwordIsRequired'),
        (v) => {
          const value = v ? v.trim() : v;
          let message = null;

          if (value) {
            if (value.length < 8) {
              message = this.$t('menus.createUser.passwordLength');
            } else if (!Helper.isValidPassword(value)) {
              message = this.$t('menus.createUser.passwordInvalidChars');
            }
          }

          return message ?? true;
        },
      ],
      nodeIdRules: [
        v => !!v || this.$t('console.nodeIdIsRequired'),
      ],
    };
  },
  watch: {
    dialog(val) {
      if (val) {
        this.resetForm();
      }
    },
    async 'formData.nodeId'(nodeId) {
      this.formData.userId = null;
      this.selectedNodeRootpath = null;

      if (!nodeId) return;

      this.isLoadingUsers = true;
      try {
        const nodeStore = useNodeStore();
        const userStore = useUserStore();
        const [nodeInfo] = await Promise.all([
          nodeStore.getNodeInfo(nodeId),
          userStore.refreshUsers({ accessPointId: nodeId }),
        ]);
        this.selectedNodeRootpath = nodeInfo.rootpath;
      } catch (error) {
        console.error('Failed to fetch node/users for access point:', error);
      } finally {
        this.isLoadingUsers = false;
      }
    },
  },
  methods: {
    resetForm() {
      const authStore = useAuthStore();
      this.formData = {
        userId: null,
        password: '',
        nodeId: authStore.getActiveAccesspoint?.node.id ?? null,
      };
      this.selectedNodeRootpath = null;
      this.errorMessage = '';
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    generatePassword() {
      const passwordLength = 10;
      this.formData.password = Helper.generatePassword(passwordLength);
    },
    copyPasswordToClipboard() {
      if (this.formData.password && this.formData.password.length > 0) {
        navigator.clipboard.writeText(this.formData.password);
      }
    },
    async submitForm() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) return;

      this.isSubmitting = true;
      this.errorMessage = '';

      try {
        const payload = {
          userid: this.formData.userId,
          password: this.formData.password,
          nodeid: this.formData.nodeId,
        };

        const ftpUserStore = useFtpUserStore();
        await ftpUserStore.createFtpUser(this.rootNodeId, payload);
        // this.$emit('created');
        this.dialog = false;
      } catch (error) {
        console.error('Failed to create FTP user:', error);
        if (error?.response?.data?.error) {
          this.errorMessage = this.$localizeMessage(error.response.data.error);
        } else {
          this.errorMessage = this.$t('backend.system-error');
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
  computed: {
    rootNodeId() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    users() {
      const userStore = useUserStore();
      const rootpath = this.selectedNodeRootpath;
      if (!rootpath) return [];
      return userStore.getUsers
        .filter(x => x.deletedat === null)
        .filter(x => x.expiredat === null)
        .filter(x => x.tenants.some(t =>
          t.accessPoints.some(ap =>
            rootpath.startsWith(ap.node.rootpath) &&
            ap.group.abilities.some(v =>
              v.action === 'belongsto' &&
              v.subject === 'Team' &&
              v.conditions?.rootnodeid === ap.node.id
            )
          )
        ));
    },
    userItems() {
      return this.users.map(u => ({
        id: u.id,
        name: `${u.name} (${u.email})`,
      }));
    },
    canAccessClipboard: function() {
      // Note: this works only on localhost and HTTPS
      return window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.writeText === 'function';
    },
  },
};
</script>
